import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { F, RA, RR } from '@macs-frontend/core/utils/fp-ts';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { makeSelectable, SelectSubscribe } from '@macs-frontend/decorators';
import { State } from '@macs-frontend/features/company-management/manage/reducers';
import { defaultUserPermissions, getUserPermissions } from '@macs-frontend/reducers/meta';
import { ListsUrls } from '@macs-frontend/web-macs/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { FormArrayState, FormGroupState } from 'ngrx-forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  BillingMethodsEnum,
  BudgetTypes,
  FinancialUtils,
  negativeNumberMask,
  ProjectTask,
  ProjectTaskGroup,
  Task,
} from '../..';
import { BudgetItemForm } from '../../../company-management/projects/models/budget-item-form';
import { PopoverDirective } from '../../directives/popover/popover.directive';
import { ProjectBudgetTypes } from '../../models/project';
import { TaskGroup } from '../../models/task-group.model';
import { TaskStatuses } from '../../models/task.model';
import { NewTasksForm, TeamTaskForm } from '../../models/team-task-forms';
import { numberConverter } from '../../utils/number.converter';
import { priceConverter } from '../../utils/price.converter';
import { AddProjectTaskComponent } from '../add-project-task/add-project-task.component';
import { DropdownComponent } from '../dropdown/dropdown.component';

export interface CreateGroupTaskPayload {
  task: Task;
  taskGroupUUID: UUID;
}

export interface ManageGroupTaskPayload {
  taskUUID: UUID;
  taskGroupUUID: UUID;
}

interface TaskWithGroup extends Task {
  group?: string;
}
type Item = FormGroupState<ProjectTask> | FormGroupState<ProjectTaskGroup>;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[mdTasksAndGroups]',
  templateUrl: './task-and-groups.component.html',
  styleUrls: ['task-and-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskAndGroupsComponent
  extends BaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Output()
  removeTaskHandler: EventEmitter<ProjectTask> = new EventEmitter<ProjectTask>();
  @Output() saveTaskHandler: EventEmitter<FormGroupState<ProjectTask>> = new EventEmitter<
    FormGroupState<ProjectTask>
  >();
  @Output() saveTaskGroupHandler: EventEmitter<FormGroupState<ProjectTaskGroup>> =
    new EventEmitter<FormGroupState<ProjectTaskGroup>>();
  @Output()
  removeTaskGroupHandler: EventEmitter<ProjectTaskGroup> = new EventEmitter<ProjectTaskGroup>();
  @Output()
  removeGroupTaskHandler: EventEmitter<ManageGroupTaskPayload> = new EventEmitter<ManageGroupTaskPayload>();
  @Output() createTaskHandler: EventEmitter<FormGroupState<ProjectTask>> =
    new EventEmitter<FormGroupState<ProjectTask>>();
  @Output() createTaskGroupHandler: EventEmitter<FormGroupState<ProjectTaskGroup>> =
    new EventEmitter<FormGroupState<ProjectTaskGroup>>();
  @Output()
  addTaskGroupToProjectHandler: EventEmitter<TaskGroup> = new EventEmitter<TaskGroup>();
  @Output()
  toggleTaskGroupHandler: EventEmitter<ProjectTaskGroup> = new EventEmitter<ProjectTaskGroup>();
  @Output()
  createTaskInGroupHandler: EventEmitter<CreateGroupTaskPayload> = new EventEmitter<CreateGroupTaskPayload>();
  @Output()
  addTaskToProjectHandler: EventEmitter<Task> = new EventEmitter<Task>();
  @Output() addNewTaskHandler: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  toggleTaskGroupBillableHandler: EventEmitter<UUID> = new EventEmitter<UUID>();
  @Output()
  toggleGroupTaskBillableHandler: EventEmitter<ManageGroupTaskPayload> = new EventEmitter<ManageGroupTaskPayload>();
  @Output() calcTaskGroup: EventEmitter<UUID> = new EventEmitter<UUID>();
  @Output() setGroupBudgetHours: EventEmitter<UUID> = new EventEmitter<UUID>();
  @Output()
  createGroupBudgetItem: EventEmitter<UUID> = new EventEmitter<UUID>();
  @Output()
  deleteGroupBudgetItemForm: EventEmitter<UUID> = new EventEmitter<UUID>();
  @Output() updateBudgetItemUnitPrice: EventEmitter<{
    uuid: UUID;
    unitPrice: number;
  }> = new EventEmitter<{ uuid: UUID; unitPrice: number }>();

  @Input()
  get teamTaskForm(): FormGroupState<TeamTaskForm> {
    return this._teamTaskForm;
  }
  set teamTaskForm(form: FormGroupState<TeamTaskForm>) {
    this._teamTaskForm = form;
    this.currentTab = form.value.budgetType;
    // @ts-expect-error
    this.tasks = Object.values(form.value.tasks.entities);
    // @ts-expect-error
    this.taskGroups = Object.values(form.value.taskGroups.entities);
    this.taskGroupNames = F.pipe(
      this.taskGroups,
      RA.map(({ originalTaskGroup: { name } }) => name),
    );

    this.taskNamesByGroup = F.pipe(
      this.taskGroups,
      RA.reduce({} as Record<string, readonly string[]>, (acc, taskGroup) => {
        acc[taskGroup.uuid.toString()] = this.parseProjectTaskNames(taskGroup.tasks);

        return acc;
      }),
    );

    this.taskNames = this.parseProjectTaskNames(this.tasks);
  }

  // @ts-expect-error
  @Input() expandedTaskGroupsEntities: Dictionary<ProjectTaskGroup>;
  // @ts-expect-error
  @Input() newTasksForm: FormGroupState<NewTasksForm>;
  // @ts-expect-error
  @Input() tasksAndGroups: (
    | FormGroupState<ProjectTask>
    | FormGroupState<ProjectTaskGroup>
  )[];

  // @ts-expect-error
  @Input() isRatesheetSelected: boolean;
  // @ts-expect-error
  @Input() budgetItemsForm: FormArrayState<BudgetItemForm>;

  // @ts-expect-error
  _teamTaskForm: FormGroupState<TeamTaskForm>;
  // @ts-expect-error
  currentTab: ProjectBudgetTypes;
  Tabs = ProjectBudgetTypes;
  BudgetTypes = BudgetTypes;
  ListsUrls = ListsUrls;
  createTaskGroupPayload = {
    status: TaskStatuses.ACTIVE,
  };
  canAddNewTask: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  canAddNewTask$: Observable<boolean> = this.canAddNewTask.asObservable();
  canAddNewTaskGroup: Subject<boolean> = new Subject<boolean>();
  canAddNewTaskGroup$: Observable<boolean> = this.canAddNewTaskGroup.asObservable();
  // @ts-expect-error
  tasks: ProjectTask[];
  // @ts-expect-error
  taskGroups: ProjectTaskGroup[];
  taskStatuses = TaskStatuses;
  negativeNumberMask = negativeNumberMask;
  priceConverter = priceConverter;
  numberConverter = numberConverter;

  get hideBudgetHours(): boolean {
    const formValue = this.teamTaskForm.value;
    return (
      formValue.budget !== BudgetTypes.HOURS_PER_TASK &&
      formValue.nonBillableBudget !== BudgetTypes.HOURS_PER_TASK &&
      formValue.nonBillableBudget !== BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK &&
      formValue.budget !== BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK
    );
  }

  @ViewChild('newTaskDropdownComponent')
  // @ts-expect-error
  newTaskDropdownComponent: DropdownComponent;
  @ViewChild('cmAddProjectTaskGroupDropdown')
  // @ts-expect-error
  addProjectTaskGroupDropdown: AddProjectTaskComponent;
  // @ts-expect-error
  @ViewChild(PopoverDirective) popover: PopoverDirective;
  @ViewChildren(DropdownComponent)
  newGroupTaskDropdown?: QueryList<DropdownComponent>;
  private deleteConfirmationText = (strings: TemplateStringsArray) =>
    `Are you sure you want to delete this ${strings[0]}?`;

  taskGroupNames: readonly string[] = [];
  taskNames: readonly string[] = [];
  taskNamesByGroup: RR.ReadonlyRecord<string, readonly string[]> = {};

  @SelectSubscribe(getUserPermissions)
  userPermissions = defaultUserPermissions;

  constructor(readonly cdr: ChangeDetectorRef, readonly store$: Store<State>) {
    super();
    makeSelectable(this, { cdr, store$ });
  }

  ngOnInit() {}

  createTaskPayload(group?: ProjectTaskGroup) {
    return {
      status: TaskStatuses.ACTIVE,
      taskGroups: (group && [group.originalTaskGroup.uuid]) || [],
    } as const;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.addProjectTaskGroupDropdown && this.newGroupTaskDropdown) {
      this.addProjectTaskGroupDropdown.filterOptions();
      this.newGroupTaskDropdown.map((dropdown: DropdownComponent) =>
        dropdown.filterOptions(),
      );
    }
    if (this.newTaskDropdownComponent) {
      this.newTaskDropdownComponent.filterOptions();
    }
  }

  removeTask(task: ProjectTask | ProjectTaskGroup) {
    if (confirm(this.deleteConfirmationText`task`)) {
      this.removeTaskHandler.emit(task as ProjectTask);
    }
  }

  saveTask(control: FormGroupState<ProjectTask>) {
    this.saveTaskHandler.emit(control);
  }

  saveTaskGroup(control: FormGroupState<ProjectTaskGroup>) {
    this.saveTaskGroupHandler.emit(control);
  }

  showBudgetHours(budget: BudgetTypes, nonBillableBudget: BudgetTypes): boolean {
    return (
      (budget !== BudgetTypes.HOURS_PER_PERSON &&
        nonBillableBudget !== BudgetTypes.HOURS_PER_PERSON) ||
      budget === BudgetTypes.HOURS_PER_TASK ||
      nonBillableBudget === BudgetTypes.HOURS_PER_TASK
    );
  }

  showBudgetFee(budget: BudgetTypes) {
    return (
      budget === BudgetTypes.FEES_PER_TASK &&
      this.currentTab !== ProjectBudgetTypes.NonBillable
    );
  }

  billingRatePerHour(billingMethod: BillingMethodsEnum) {
    return (
      billingMethod === BillingMethodsEnum.TASK_HOURLY_RATE &&
      this.currentTab !== ProjectBudgetTypes.NonBillable &&
      this.currentTab !== ProjectBudgetTypes.FixedFee
    );
  }

  removeTaskGroup(taskGroup: ProjectTaskGroup | ProjectTask) {
    if (confirm(this.deleteConfirmationText`task group`)) {
      this.removeTaskGroupHandler.emit(taskGroup as unknown as ProjectTaskGroup);
    }
  }

  removeGroupTask(taskUUID: UUID, taskGroupUUID: UUID) {
    if (confirm(this.deleteConfirmationText`task`)) {
      this.removeGroupTaskHandler.emit({ taskUUID, taskGroupUUID });
    }
  }

  createTask(control: FormGroupState<ProjectTask>) {
    this.createTaskHandler.emit(control);
  }

  createTaskGroup(control: FormGroupState<ProjectTaskGroup>) {
    this.createTaskGroupHandler.emit(control);
  }

  addTaskGroupToProject(taskGroup: TaskGroup) {
    this.addTaskGroupToProjectHandler.emit(taskGroup);
    this.popover.togglePopup(false);
  }

  toggleTaskGroup(taskGroup: ProjectTaskGroup) {
    this.toggleTaskGroupHandler.emit(taskGroup);
  }

  createTaskInGroup(task: Task, taskGroupUUID: UUID) {
    if (task) {
      this.createTaskInGroupHandler.emit({ task, taskGroupUUID });
      this.newGroupTaskDropdown?.map((dropdown: DropdownComponent) =>
        dropdown.setInputValue(''),
      );
    }
  }

  filterTasks = (tasks: TaskWithGroup[]): TaskWithGroup[] => {
    // eslint-disable-next-line no-param-reassign
    tasks = tasks.map(task => ({
      ...task,
      group: task.defaultBillable ? 'Billable' : 'Non billable',
    }));

    // to make billable tasks first and non billable tasks second
    return [
      ...tasks.filter(task => task.group === 'Billable'),
      ...tasks.filter(task => task.group === 'Non billable'),
    ];
  };

  addTaskToProject(task: Task) {
    this.addTaskToProjectHandler.emit(task);
  }

  addNewTask() {
    this.popover.togglePopup(false);
    return !this.newTasksForm.value.tasks.length ? this.addNewTaskHandler.emit() : null;
  }

  onTaskFind(query: string): boolean {
    const isTaskExist: boolean = this.isTaskExist(query);
    this.canAddNewTask.next(!isTaskExist);
    return isTaskExist;
  }

  canAddTask(dropdown: DropdownComponent): boolean {
    return (
      (dropdown.input ? !this.isTaskExist(dropdown.input.nativeElement.value) : true) &&
      this.userPermissions.OBJECTS_TASK_CREATE
    );
  }

  isTaskExist(query: string): boolean {
    return (
      this.findTaskInTasks(this.tasks, query) ||
      !!this.taskGroups.find((taskGroup: ProjectTaskGroup) =>
        this.findTaskInTasks(taskGroup.tasks, query),
      )
    );
  }

  onTaskGroupFind(query: string) {
    const isTaskGroupExist = !!Object.values(
      this.teamTaskForm.value.taskGroups.entities,
    ).find(
      taskGroup =>
        taskGroup?.originalTaskGroup.name.toLowerCase() === query.toLowerCase(),
    );
    this.canAddNewTaskGroup.next(!isTaskGroupExist);
  }

  findTaskInTasks(tasks: ProjectTask[], name: string): boolean {
    return !!tasks.find(
      (task: ProjectTask) =>
        // @ts-expect-error
        task.originalTask.name.toLowerCase() === name.toLowerCase(),
    );
  }

  parseProjectTaskNames(tasks?: ProjectTask[]): string[] {
    // @ts-expect-error
    return (tasks || []).map((task: ProjectTask) => task.originalTask.name);
  }

  entitiesToArray(entities: FormGroupState<Dictionary<any>>) {
    return Object.values(entities);
  }

  toggleTaskGroupBillable(taskGroupUUID: UUID) {
    this.toggleTaskGroupBillableHandler.emit(taskGroupUUID);
  }

  toggleGroupTaskBillable(taskGroupUUID: UUID, taskUUID: UUID) {
    this.toggleGroupTaskBillableHandler.emit({ taskGroupUUID, taskUUID });

    setTimeout((_: any) => this.calcTaskGroup.emit(taskGroupUUID));
  }

  taskGroupBillableIndeterminate(taskGroup: ProjectTaskGroup): boolean {
    const billableTaskAmount: number = taskGroup.tasks.filter(
      (task: ProjectTask) => task.billable,
    ).length;
    // @ts-expect-error
    return billableTaskAmount && taskGroup.tasks.length > billableTaskAmount;
  }

  onSetGroupHours(taskGroup: ProjectTaskGroup) {
    if (
      !taskGroup.budgetItem ||
      !taskGroup.budgetItem.uuid ||
      !this.budgetItemsForm.value.some(form => form.uuid === taskGroup.budgetItem?.uuid)
    ) {
      this.createGroupBudgetItem.emit(taskGroup.uuid);
    }
  }

  onSetGroupTaskHours(controlId: string) {
    setTimeout((_: any) => {
      // @ts-expect-error
      const taskGroup = (this.tasksAndGroups as FormGroupState<ProjectTaskGroup>[]).find(
        item => item.id === controlId,
      ).value as ProjectTaskGroup;
      if (
        taskGroup.tasks.some(task => !!+task.budgetHours) &&
        taskGroup.budgetItem &&
        taskGroup.budgetItem.uuid
      ) {
        this.deleteGroupBudgetItemForm.emit(taskGroup.budgetItem.uuid);
      }
      this.setGroupBudgetHours.emit(taskGroup.uuid);
    });
  }

  onBillableRateChange(item: Item) {
    const itemValue = item.value;
    const budgetItem = this.budgetItemsForm.value.find(
      _budgetItem =>
        itemValue.budgetItem &&
        itemValue.budgetItem.uuid &&
        _budgetItem.uuid === itemValue.budgetItem.uuid,
    );
    const unitPrice = FinancialUtils.toCents(item.value.billingRatePerHour);
    if (budgetItem && !budgetItem.unitPrice && unitPrice) {
      // @ts-expect-error
      const uuid = item.value.budgetItem.uuid;
      this.updateBudgetItemUnitPrice.emit({ uuid, unitPrice });
    }
  }

  isTaskGroupValidBudgetItem(group: ProjectTaskGroup): boolean {
    // @ts-expect-error
    return (
      group.budgetItem &&
      group.budgetItem.uuid &&
      !group.tasks.some(task => !!+task.budgetHours)
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.canAddNewTask.complete();
    this.canAddNewTaskGroup.complete();
  }

  getTaskGroupForm(
    form: FormGroupState<ProjectTaskGroup> | FormGroupState<ProjectTask>,
  ): FormGroupState<ProjectTaskGroup> {
    return form as FormGroupState<ProjectTaskGroup>;
  }

  getTaskGroupTotalAmount(
    form: FormGroupState<ProjectTaskGroup> | FormGroupState<ProjectTask>,
  ): number {
    return (form as FormGroupState<ProjectTaskGroup>).value.tasks.reduce(
      (acc, task) => acc + task.budgetHours * task.billingRatePerHour,
      0,
    );
  }

  getTaskForm(
    form: FormGroupState<ProjectTask> | FormGroupState<ProjectTaskGroup>,
  ): FormGroupState<ProjectTask> {
    return form as FormGroupState<ProjectTask>;
  }
}
